import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button, IconClose, Typography } from '@grammarly/badger-ui'

import { Card } from './card'
import { Form } from './form'
import { Modal } from '../../ui-lib/modal'
import { SignUpModalContent } from '../../ui-lib/modal/signup'
import { FreeUserTrialModal } from '../../ui-lib/modal/free-user-trial-modal'
import { ToolExitIntentModal } from '../../tool/components/tool-modals'

import { gnarSpec } from '../../../lib/tracking/gnar'
import { PageContext } from '../../../contexts/page-context'
import { PageSection } from '../../page-section'
import { ContentProps } from '../../renderer'
import { renderRichTextMultiLineContent } from '../../renderer/rich-text'
import {
  GoTemplatesLayoutType,
  isLlmPromptReferenceToEntry,
  MultiStepProps,
  TypeSectionGoTemplatesTool,
} from '../../../lib/types'
import { CapiWsProvider } from '../../../lib/services/api/cheetah-provider'
import { useShouldTriggerWall } from './use-should-trigger-wall'
import { useExperimentService, useUserService, useFreeLimitToTrialExperiment } from '../../../lib/hooks'
import { useExitIntentModal } from '../../../lib/hooks/experiments/use-exit-intent-modal'
import { SingleColumnCtaCardContent, SingleColumnWallContent } from './card/card-content'
import { SideBySideForm } from './side-by-side'
import { GoTemplatesProvider } from './go-templates-context'
import {
  maxCountSubmitAnonymous,
  maxCountSubmitPremium,
  maxCountSubmitSignedIn,
  useUpdateCountSubmit,
} from './local-storage-state'
import {
  getExpOptimalUsageLimit,
  isToolsOptimalUsageLimitTestTreatment,
  UrlBasedUsageTracker,
} from '../../../lib/utils/tool-usage'
import { SectionContext } from '@contexts/section-context'
import { CompactSectionsContext } from '@contexts/compact-sections-context'
import { Tool } from '@components/tool'
import { AI_HUMANIZER_SLUG, PARAPHRASING_TOOL_SLUG } from '@lib/constants'

import styles from './go-templates-tool.module.scss'
import { PROFESSIONAL_TOOL_SLUGS } from '@lib/constants'
import { useIntl } from 'react-intl'

const { T2, C2 } = Typography

const MultiStep: React.FC<MultiStepProps> = ({ title, description, questions, persistentCtaCardContent }) => {
  const { isH1Section } = useContext(SectionContext)

  return (
    <>
      <div className={styles.container}>
        <Card className={styles.tool}>
          {title && <T2 as={isH1Section ? 'h1' : 'h2'}>{renderRichTextMultiLineContent(title)}</T2>}
          {description && <C2 as="div">{renderRichTextMultiLineContent(description)}</C2>}
          {(title || description) && <hr className={styles.divider} />}
          <Form questions={questions} />
        </Card>
        <Card className={styles.persistentCtaCard}>
          <SingleColumnCtaCardContent content={persistentCtaCardContent} />
        </Card>
      </div>
    </>
  )
}

export const SectionGoTemplatesTool: React.FC<ContentProps<TypeSectionGoTemplatesTool>> = ({
  className,
  structure,
}) => {
  const {
    sys,
    fields: {
      internalName,
      title,
      description,
      background,
      questions,
      persistentCtaCard,
      signupWall,
      upgradeWall,
      llmPrompt,
      outputLabel,
      outputDescription,
      submitButtonText,
      loadingButtonText,
      layout,
    },
  } = structure

  const { currentUser } = useUserService()
  const { isAnonymous, isPremium } = currentUser
  const { pageId, slug } = useContext(PageContext)
  const intl = useIntl()
  const { isGoogleCtaSignupWallV2, peekToolsOptimalUsageLimitAssignment, isTools2_XWithLoggedInEnabled } =
    useExperimentService()

  const peekedToolsOptimalUsageLimitAssignment = peekToolsOptimalUsageLimitAssignment(isAnonymous, slug)
  const isOptimalUsageLimitTestTreatment = isToolsOptimalUsageLimitTestTreatment(peekedToolsOptimalUsageLimitAssignment)

  const {
    usageTracker: freeTrialUsageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsFreeUseLimitExperiment,
  } = useFreeLimitToTrialExperiment()

  const maxCountAnonymousWithOverride = PROFESSIONAL_TOOL_SLUGS.includes(slug) ? 100 : maxCountSubmitAnonymous

  const goTemplatesMaxCountSubmit = isPremium
    ? maxCountSubmitPremium
    : isAnonymous
      ? maxCountAnonymousWithOverride
      : maxCountSubmitSignedIn

  const goTemplatesUsageTracker = useMemo(() => {
    if (isOptimalUsageLimitTestTreatment) {
      const testUsageLimit = getExpOptimalUsageLimit(peekedToolsOptimalUsageLimitAssignment, maxCountSubmitAnonymous)
      return new UrlBasedUsageTracker(testUsageLimit, false, undefined, currentUser)
    }
    if (freeTrialUsageTracker) {
      return freeTrialUsageTracker
    }
    return new UrlBasedUsageTracker(goTemplatesMaxCountSubmit, true, 'go-templates', currentUser)
  }, [
    isOptimalUsageLimitTestTreatment,
    freeTrialUsageTracker,
    peekedToolsOptimalUsageLimitAssignment,
    currentUser,
    goTemplatesMaxCountSubmit,
  ])

  const isSideBySide = layout === GoTemplatesLayoutType.SideBySide && questions.length === 1
  const wallType = isAnonymous ? 'signup' : 'upgrade'
  const wallContent = wallType === 'signup' ? signupWall.fields.content : upgradeWall.fields.content
  const { content: persistentCtaCardContent } = persistentCtaCard.fields
  // TODO: Clean up after migration. LLM Prompt will be a reference to an entry once migration is completed.
  const prompt = isLlmPromptReferenceToEntry(llmPrompt) ? llmPrompt.fields.prompt : llmPrompt

  const [isWallOpen, setIsWallOpen] = useState(false)

  const shouldTriggerWall = useShouldTriggerWall(goTemplatesUsageTracker)
  const triggerWall = useCallback(() => {
    if (isToolsFreeUseLimitExperiment) {
      setShowTrialModal(true)
      return
    }
    setIsWallOpen(true)
  }, [isToolsFreeUseLimitExperiment, setShowTrialModal])
  const updateCountSubmit = useUpdateCountSubmit(isAnonymous)

  const isGoogleCtaSignupWallV2Enabled = wallType === 'signup' && isWallOpen && isGoogleCtaSignupWallV2(isAnonymous)
  const isToolsRedesignEnabled = isTools2_XWithLoggedInEnabled()

  const { inExitIntentExperiment, isExitModalOpen, closeExitModal } = useExitIntentModal()

  useEffect(() => {
    if (isWallOpen) {
      gnarSpec.goToolsWallShow(pageId, slug, wallType)
    }
  }, [isWallOpen, pageId, slug, wallType])

  if (isSideBySide && isToolsRedesignEnabled) {
    const outputTitle =
      slug === AI_HUMANIZER_SLUG
        ? intl.formatMessage({
            id: 'goTemplatesTool.outputTitle.aiHumanizerSlug',
            description: 'Go Templates Tool - Output Title - AI Humanizer Slug',
            defaultMessage: 'Humanized text',
          })
        : slug === PARAPHRASING_TOOL_SLUG
          ? intl.formatMessage({
              id: 'goTemplatesTool.outputTitle.paraphrasingToolSlug',
              description: 'Go Templates Tool - Output Title - Paraphrasing Tool Slug',
              defaultMessage: 'Paraphrased Text',
            })
          : intl.formatMessage({
              id: 'goTemplatesTool.outputTitle.default',
              description: 'Go Templates Tool - Output Title - Default',
              defaultMessage: 'AI Writing Tool results',
            })

    const maxWordLimit = slug === PARAPHRASING_TOOL_SLUG ? 125 : 1500

    const toolSubmitButtonText =
      slug === AI_HUMANIZER_SLUG
        ? intl.formatMessage({
            id: 'goTemplatesTool.submitButtonText.aiHumanizerSlug',
            description: 'Go Templates Tool - Submit Button Text - AI Humanizer Slug',
            defaultMessage: 'Humanize',
          })
        : slug === PARAPHRASING_TOOL_SLUG
          ? intl.formatMessage({
              id: 'goTemplatesTool.submitButtonText.paraphrasingToolSlug',
              description: 'Go Templates Tool - Submit Button Text - Paraphrasing Tool Slug',
              defaultMessage: 'Paraphrase',
            })
          : undefined

    const toolType =
      slug === AI_HUMANIZER_SLUG ? 'humanizer' : slug === PARAPHRASING_TOOL_SLUG ? 'paraphraser' : 'goTemplatesGeneric'

    return (
      <CompactSectionsContext.Provider value={true}>
        <PageSection
          internalName={internalName}
          sysStructure={sys}
          className={className}
          background={background}
          heading={isSideBySide ? structure : undefined}
          isToolsRedesign2_XEnabled
        >
          <Tool
            toolType={toolType}
            outputTitle={outputTitle}
            maxWordLimit={maxWordLimit}
            minWordLimit={0}
            llmPrompt={prompt}
            inputName={questions[0].fields.name}
            submitButtonText={toolSubmitButtonText}
            anonymousUsageLimit={1}
            sampleText={questions[0].fields.sampleText}
          />
        </PageSection>
      </CompactSectionsContext.Provider>
    )
  }

  return (
    <PageSection
      internalName={internalName}
      sysStructure={sys}
      className={className}
      background={background}
      heading={isSideBySide ? structure : undefined}
      isToolsRedesign2_XEnabled={isToolsRedesignEnabled}
    >
      <CapiWsProvider
        skip={shouldTriggerWall}
        onTriggerWall={triggerWall}
        onCheetahActionSend={updateCountSubmit}
        usageTracker={goTemplatesUsageTracker}
      >
        <GoTemplatesProvider
          llmPrompt={prompt}
          submitButtonText={submitButtonText}
          loadingButtonText={loadingButtonText}
          outputLabel={outputLabel}
          outputDescription={outputDescription}
          internalName={internalName}
          isWallOpen={isWallOpen}
          triggerWall={triggerWall}
          usageTracker={goTemplatesUsageTracker}
        >
          {isSideBySide ? (
            <SideBySideForm question={questions[0]} />
          ) : (
            <MultiStep
              title={title}
              description={description}
              questions={questions}
              persistentCtaCardContent={persistentCtaCardContent}
            />
          )}
          <Modal className={styles.signupWallModal} isOpen={isWallOpen} onRequestClose={() => setIsWallOpen(false)}>
            <Card className={styles.signupWall}>
              <Button
                className={styles.signupWallClose}
                padding="none"
                onClick={() => setIsWallOpen(false)}
                aria-label={intl.formatMessage({
                  id: 'global.uiAction.close',
                  description: 'Text for CTA that closes an element',
                  defaultMessage: 'Close',
                })}
              >
                <IconClose aria-hidden={true} />
              </Button>
              {isGoogleCtaSignupWallV2Enabled ? (
                <SignUpModalContent />
              ) : (
                <SingleColumnWallContent content={wallContent} />
              )}
            </Card>
          </Modal>
          {!isWallOpen && <FreeUserTrialModal show={showTrialModal} close={() => setShowTrialModal(false)} />}
          {inExitIntentExperiment && <ToolExitIntentModal isOpen={isExitModalOpen} onClose={closeExitModal} />}
        </GoTemplatesProvider>
      </CapiWsProvider>
    </PageSection>
  )
}
